/**
 * 管理员视角的成片合成任务列表（含 ffmpeg 错误日志 + 重试）
 */

import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, X as XIcon } from 'lucide-react';
import toast from '../../Toast';
import { formatBeijingDateTime } from '../../../utils/time';
import {
  directorAdminApi,
  type AdminDirectorComposition,
} from '../../../apps/director/services/directorAdminApi';

const STATUS_FILTERS = [
  { value: '', label: '全部' },
  { value: 'pending', label: '排队中' },
  { value: 'running', label: '处理中' },
  { value: 'completed', label: '已完成' },
  { value: 'failed', label: '失败' },
];

export default function DirectorCompositionsTab() {
  const [data, setData] = useState<AdminDirectorComposition[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [detailOpen, setDetailOpen] = useState<AdminDirectorComposition | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const result = await directorAdminApi.listCompositions({
        page,
        pageSize,
        status: statusFilter || undefined,
      });
      setData(result.data);
      setTotal(result.pagination.total);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const handleRetry = async (id: string) => {
    if (!window.confirm('重试将创建一条新的合成任务，原任务保留为失败记录。继续吗？')) return;
    try {
      await directorAdminApi.retryComposition(id);
      toast.success('已创建新任务');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '重试失败');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="input"
        >
          {STATUS_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button onClick={load} className="btn-secondary px-3 py-1.5">
          刷新
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left">
                <th className="px-4 py-2 font-medium">项目</th>
                <th className="px-4 py-2 font-medium">状态</th>
                <th className="px-4 py-2 font-medium">配置</th>
                <th className="px-4 py-2 font-medium">时长</th>
                <th className="px-4 py-2 font-medium">创建时间</th>
                <th className="px-4 py-2 font-medium text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-light-muted">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                    加载中
                  </td>
                </tr>
              )}
              {!loading && data.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-light-muted">
                    暂无数据
                  </td>
                </tr>
              )}
              {data.map((c) => (
                <tr key={c.id} className="border-b">
                  <td className="px-4 py-2">
                    <div className="font-medium">{c.project.name}</div>
                    <div className="text-xs text-light-muted">用户 {c.project.userId.slice(0, 8)}</div>
                  </td>
                  <td className="px-4 py-2">
                    <StatusPill status={c.status} />
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <div>{c.outputResolution} / {c.aspectRatio}</div>
                    <div className="text-light-muted">
                      {c.transitionType === 'fade' ? `淡入淡出 ${c.transitionDurationMs}ms` : '无转场'}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {c.resultDuration ? `${c.resultDuration.toFixed(1)}s` : '—'}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {formatBeijingDateTime(c.createdAt)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <CompositionActions
                      composition={c}
                      onDetail={setDetailOpen}
                      onRetry={handleRetry}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="divide-y lg:hidden">
          {loading ? (
            <div className="px-4 py-8 text-center text-light-muted">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              加载中
            </div>
          ) : data.length === 0 ? (
            <div className="px-4 py-8 text-center text-light-muted">暂无数据</div>
          ) : (
            data.map((c) => (
              <div key={c.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-light-text">{c.project.name}</div>
                    <div className="mt-1 text-xs text-light-muted">用户 {c.project.userId.slice(0, 8)}</div>
                  </div>
                  <StatusPill status={c.status} />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <InfoBlock label="配置" value={`${c.outputResolution} / ${c.aspectRatio}`} />
                  <InfoBlock
                    label="时长"
                    value={c.resultDuration ? `${c.resultDuration.toFixed(1)}s` : '—'}
                  />
                </div>
                <div className="text-xs text-light-muted">
                  转场：{c.transitionType === 'fade' ? `淡入淡出 ${c.transitionDurationMs}ms` : '无转场'}
                </div>
                <div className="text-xs text-light-muted">
                  创建时间：{formatBeijingDateTime(c.createdAt)}
                </div>
                <CompositionActions
                  composition={c}
                  onDetail={setDetailOpen}
                  onRetry={handleRetry}
                  mobile
                />
              </div>
            ))
          )}
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-light-muted">共 {total} 条</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary px-3 py-1 disabled:opacity-50"
          >
            上一页
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-secondary px-3 py-1 disabled:opacity-50"
          >
            下一页
          </button>
        </div>
      </div>

      {/* 详情弹窗：含 errorMessage（管理员可见） */}
      {detailOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setDetailOpen(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-semibold">合成任务详情</h2>
              <button onClick={() => setDetailOpen(null)} className="p-1 hover:bg-gray-100 rounded">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div>
                <span className="text-light-muted">ID：</span>
                <code className="text-xs">{detailOpen.id}</code>
              </div>
              <div>
                <span className="text-light-muted">项目：</span>
                {detailOpen.project.name}
              </div>
              <div>
                <span className="text-light-muted">状态：</span>
                {detailOpen.status}
              </div>
              <div>
                <span className="text-light-muted">配置：</span>
                {detailOpen.outputResolution} / {detailOpen.aspectRatio} / {detailOpen.transitionType}
              </div>
              {detailOpen.errorMessage && (
                <div>
                  <div className="text-red-600 font-medium mb-1">错误信息（仅管理员可见）</div>
                  <pre className="bg-red-50 border border-red-200 p-3 rounded text-xs whitespace-pre-wrap break-all max-h-96 overflow-y-auto">
                    {detailOpen.errorMessage}
                  </pre>
                </div>
              )}
              {detailOpen.shotsSnapshot && (
                <details>
                  <summary className="cursor-pointer text-blue-600">分镜快照</summary>
                  <pre className="bg-gray-50 border p-3 rounded text-xs overflow-x-auto mt-2">
                    {JSON.stringify(JSON.parse(detailOpen.shotsSnapshot), null, 2)}
                  </pre>
                </details>
              )}
              {detailOpen.shotsTimingSnapshot && (
                <details>
                  <summary className="cursor-pointer text-blue-600">时间戳快照</summary>
                  <pre className="bg-gray-50 border p-3 rounded text-xs overflow-x-auto mt-2">
                    {JSON.stringify(JSON.parse(detailOpen.shotsTimingSnapshot), null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs ${
        status === 'completed'
          ? 'bg-green-100 text-green-700'
          : status === 'failed'
            ? 'bg-red-100 text-red-700'
            : 'bg-blue-100 text-blue-700'
      }`}
    >
      {status}
    </span>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className="mt-1 text-sm text-light-text">{value}</div>
    </div>
  );
}

function CompositionActions({
  composition,
  onDetail,
  onRetry,
  mobile = false,
}: {
  composition: AdminDirectorComposition;
  onDetail: (value: AdminDirectorComposition) => void;
  onRetry: (id: string) => Promise<void>;
  mobile?: boolean;
}) {
  return (
    <div className={mobile ? 'flex flex-wrap gap-2' : 'space-x-1'}>
      <button
        onClick={() => onDetail(composition)}
        className={mobile
          ? 'inline-flex items-center rounded-lg border border-blue-200 px-3 py-2 text-xs text-blue-600 hover:bg-blue-50'
          : 'text-blue-600 hover:bg-blue-50 px-2 py-1 rounded text-xs'}
      >
        详情
      </button>
      {composition.status === 'failed' && (
        <button
          onClick={() => void onRetry(composition.id)}
          className={mobile
            ? 'inline-flex items-center gap-1 rounded-lg border border-amber-200 px-3 py-2 text-xs text-amber-600 hover:bg-amber-50'
            : 'text-amber-600 hover:bg-amber-50 px-2 py-1 rounded text-xs flex items-center gap-1 inline-flex'}
        >
          <RefreshCw className="w-3 h-3" /> 重试
        </button>
      )}
    </div>
  );
}
